<template>
  <main class="public-info-page" :class="[theme, { 'contact-page': pageKey === 'contact', 'faq-page': pageKey === 'faq', 'about-page': pageKey === 'about' }]" dir="rtl">
    <header class="public-header">
      <router-link :to="{ name: 'HomePage' }" class="brand-link">
        <img v-if="siteSettings.site_logo" :src="`/storage/settings/${siteSettings.site_logo}`" :alt="siteName" class="brand-logo" />
        <VsxIcon v-else iconName="Chart" :size="28" color="#3C278E" type="linear" />
        <span>{{ siteName }}</span>
      </router-link>
      <nav class="page-nav" aria-label="صفحات راهنما">
        <router-link :to="{ name: 'AboutPage' }">درباره ما</router-link>
        <router-link :to="{ name: 'FaqPage' }">پرسش‌های متداول</router-link>
        <router-link :to="{ name: 'JourneyPage' }">مسیر استفاده</router-link>
        <router-link :to="{ name: 'ContactPage' }">تماس</router-link>
      </nav>
      <button type="button" class="back-link" @click="goToSoftware">
        <VsxIcon iconName="ArrowRight2" :size="18" color="currentColor" type="linear" />
        بازگشت به نرم‌افزار
      </button>
    </header>

    <section v-if="pageKey === 'about'" class="about-hero">
      <div class="about-hero-copy">
        <span class="about-eyebrow"><VsxIcon iconName="InfoCircle" :size="17" color="currentColor" type="linear" />درباره ما</span>
        <h1>داده پردازان</h1>
        <p>با رویکردی شفاف و اصولی، با خدمات داده‌پردازان بیشتر آشنا شوید.</p>
      </div>
      <div class="about-hero-visual" aria-label="نشان داده‌پردازان در کنار نمودار مالی تزئینی">
        <div class="about-chart-grid" aria-hidden="true">
          <span class="about-chart-line about-chart-line--one"></span>
          <span class="about-chart-line about-chart-line--two"></span>
          <span class="about-chart-bar about-chart-bar--one"></span>
          <span class="about-chart-bar about-chart-bar--two"></span>
          <span class="about-chart-bar about-chart-bar--three"></span>
          <span class="about-chart-bar about-chart-bar--four"></span>
          <span class="about-chart-bar about-chart-bar--five"></span>
        </div>
        <div class="about-logo-plinth">
          <img src="../assets/img/lightL.jpg" alt="نشان داده‌پردازان" />
        </div>
        <span class="about-visual-orbit about-visual-orbit--one" aria-hidden="true"></span>
        <span class="about-visual-orbit about-visual-orbit--two" aria-hidden="true"></span>
        <span class="about-visual-spark about-visual-spark--one" aria-hidden="true"></span>
        <span class="about-visual-spark about-visual-spark--two" aria-hidden="true"></span>
      </div>
    </section>

    <section v-else class="page-hero" :class="{ 'faq-hero': pageKey === 'faq' }">
      <div class="hero-copy">
        <span class="eyebrow">{{ page.eyebrow }}</span>
        <h1 v-if="pageKey === 'contact'" class="contact-title">
          <span>پشتیبانی و</span>
          <span class="contact-title-accent">ارتباط با ما</span>
        </h1>
        <h1 v-else>{{ page.title }}</h1>
        <p>{{ page.intro }}</p>
      </div>
      <div v-if="pageKey === 'faq'" class="faq-hero-art" aria-hidden="true">
        <svg viewBox="0 0 260 220" role="presentation">
          <defs>
            <linearGradient id="faqQuestionFront" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stop-color="#a58af0" />
              <stop offset="1" stop-color="#6043bd" />
            </linearGradient>
            <linearGradient id="faqQuestionSide" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0" stop-color="#51369f" />
              <stop offset="1" stop-color="#392276" />
            </linearGradient>
            <linearGradient id="faqQuestionOrange" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stop-color="#ffbd7a" />
              <stop offset="1" stop-color="#e47a43" />
            </linearGradient>
            <filter id="faqQuestionShadow" x="-30%" y="-30%" width="170%" height="180%">
              <feDropShadow dx="0" dy="12" stdDeviation="9" flood-color="#4b368b" flood-opacity=".2" />
            </filter>
          </defs>
          <ellipse cx="132" cy="193" rx="91" ry="13" fill="#6f59ba" opacity=".1" />
          <g filter="url(#faqQuestionShadow)" transform="translate(42 3) rotate(-10 90 105)">
            <path d="M100 28c-28 0-49 18-50 44h32c1-10 7-16 18-16 10 0 17 6 17 15 0 8-5 13-16 21-17 12-25 23-25 42v5h32v-3c0-10 4-15 17-24 17-12 26-23 26-43 0-23-20-41-51-41Z" fill="url(#faqQuestionSide)" transform="translate(10 6)" />
            <path d="M100 28c-28 0-49 18-50 44h32c1-10 7-16 18-16 10 0 17 6 17 15 0 8-5 13-16 21-17 12-25 23-25 42v5h32v-3c0-10 4-15 17-24 17-12 26-23 26-43 0-23-20-41-51-41Z" fill="url(#faqQuestionFront)" />
            <ellipse cx="105" cy="171" rx="18" ry="17" fill="url(#faqQuestionSide)" transform="translate(8 5)" />
            <ellipse cx="105" cy="171" rx="18" ry="17" fill="url(#faqQuestionFront)" />
          </g>
          <g filter="url(#faqQuestionShadow)" transform="translate(150 60) rotate(11 36 62) scale(.58)">
            <path d="M100 28c-28 0-49 18-50 44h32c1-10 7-16 18-16 10 0 17 6 17 15 0 8-5 13-16 21-17 12-25 23-25 42v5h32v-3c0-10 4-15 17-24 17-12 26-23 26-43 0-23-20-41-51-41Z" fill="#bf6841" transform="translate(10 6)" />
            <path d="M100 28c-28 0-49 18-50 44h32c1-10 7-16 18-16 10 0 17 6 17 15 0 8-5 13-16 21-17 12-25 23-25 42v5h32v-3c0-10 4-15 17-24 17-12 26-23 26-43 0-23-20-41-51-41Z" fill="url(#faqQuestionOrange)" />
            <ellipse cx="105" cy="171" rx="18" ry="17" fill="#bf6841" transform="translate(8 5)" />
            <ellipse cx="105" cy="171" rx="18" ry="17" fill="url(#faqQuestionOrange)" />
          </g>
          <circle cx="40" cy="66" r="5" fill="#ee995c" />
          <circle cx="218" cy="154" r="4" fill="#8b73d3" />
          <path d="m208 39 4 9 9 4-9 4-4 9-4-9-9-4 9-4z" fill="#f0a35f" opacity=".88" />
        </svg>
        <span class="faq-art-orbit faq-art-orbit--one"></span>
        <span class="faq-art-orbit faq-art-orbit--two"></span>
      </div>
      <div v-else-if="pageKey !== 'contact'" class="hero-mark" aria-hidden="true">
        <VsxIcon :iconName="page.icon" :size="54" color="#3C278E" type="linear" />
      </div>
    </section>

    <section v-if="pageKey === 'about'" class="content-section about-content">
      <article class="about-company-card">
        <div class="about-company-copy">
          <p>داده‌پردازان، یک شرکت خصوصی فعال در حوزه مالی و سرمایه‌گذاری است که با هدف ارائه خدمات حرفه‌ای و نوین در بازارهای مالی، فعالیت خود را آغاز کرده است. ما با تکیه بر تجربه، دانش تخصصی و تیمی مجرب، تلاش می‌کنیم تا بهترین فرصت‌ها را برای رشد و افزایش سرمایه شما فراهم کنیم.</p>
        </div>
        <div class="about-company-values">
          <span class="about-people-icon"><VsxIcon iconName="People" :size="34" color="currentColor" type="linear" /></span>
          <strong>تجربه، تخصص، اعتماد</strong>
        </div>
      </article>

      <section class="about-licenses" aria-labelledby="about-licenses-title">
        <div class="about-section-title">
          <span class="about-title-rule" aria-hidden="true"></span>
          <div>
            <h2 id="about-licenses-title">مجوزها و مدارک قانونی</h2>
            <p>داده‌پردازان با بهره‌مندی از مجوزهای رسمی و معتبر، در چارچوب قوانین جمهوری اسلامی ایران فعالیت می‌کند.</p>
          </div>
          <span class="about-title-rule" aria-hidden="true"></span>
        </div>

        <div class="about-license-grid">
          <article v-for="document in legalDocuments" :key="document.title" class="about-license-card">
            <div class="about-license-image">
              <img :src="document.image" :alt="document.title" />
            </div>
            <div class="about-license-caption">
              <VsxIcon iconName="DocumentText" :size="21" color="#fff" type="linear" />
              <h3>{{ document.title }}</h3>
            </div>
          </article>
        </div>
      </section>

      <aside class="about-contact-banner">
        <div class="about-headset-art" role="img" aria-label="تصویرسازی تزئینی هدست پشتیبانی؛ فایل تصویر اصلی موجود نیست">
          <span class="about-headset-orbit"></span>
          <span class="about-headset-bubble about-headset-bubble--one"><VsxIcon iconName="Messages1" :size="21" color="#fff" type="linear" /></span>
          <span class="about-headset-bubble about-headset-bubble--two"><VsxIcon iconName="MessageText1" :size="17" color="#fff" type="linear" /></span>
          <span class="about-headset-body"></span>
          <span class="about-headset-face"></span>
          <span class="about-headset-band"></span>
          <span class="about-headset-ear"></span>
          <span class="about-headset-mic"></span>
        </div>
        <div class="about-contact-copy">
          <h2>اعتماد شما، سرمایه ماست</h2>
          <p>برای کسب اطلاعات بیشتر و مشاوره می‌توانید از طریق راه‌های ارتباطی با ما در تماس باشید.</p>
        </div>
        <router-link class="about-contact-link" :to="{ name: 'ContactPage' }">
          <span>ارتباط با ما</span>
          <VsxIcon iconName="ArrowLeft2" :size="19" color="currentColor" type="linear" />
        </router-link>
      </aside>

      <section class="about-why-continuation" aria-labelledby="about-why-title">
        <div class="about-why-copy">
          <span class="about-continuation-eyebrow">مسیر ما</span>
          <h2 id="about-why-title">چرا داده‌پردازان؟</h2>
          <p>داده‌پردازان، با بخش خصوصی و واقعی در حوزه بازار سرمایه نقد داریم. با این حال خود را در همه حال مقید به رعایت کلیه مقررات داخلی و بین‌المللی و همچنین طور شفافیت حداکثری با نهادهای ناظر و مشتریان خود می‌دانیم.</p>
        </div>
        <div class="about-why-visual" role="img" aria-label="جایگاه تصویر شخصیت سه‌بعدی با لپ‌تاپ؛ تصویر اصلی هنوز ارائه نشده است">
          <span class="about-why-glow"></span>
          <span class="about-why-orbit about-why-orbit--one"></span>
          <span class="about-why-orbit about-why-orbit--two"></span>
          <span class="about-why-placeholder-icon"><VsxIcon iconName="Monitor" :size="45" color="currentColor" type="linear" /></span>
          <span class="about-why-placeholder-label">تصویر شخصیت سه‌بعدی در دسترس نیست</span>
          <span class="about-why-spark about-why-spark--one"></span>
          <span class="about-why-spark about-why-spark--two"></span>
        </div>
      </section>

      <section class="about-story-cards" aria-label="تیم و محیط کاری داده‌پردازان">
        <article class="about-story-card about-story-card--team">
          <div class="about-story-photo">
            <img src="/img/about3.jpg" alt="تصویر تیم داده‌پردازان در محیط شرکت" loading="lazy" />
          </div>
          <div class="about-story-copy">
            <span class="about-story-icon"><VsxIcon iconName="People" :size="25" color="currentColor" type="linear" /></span>
            <h2>همراه یک تیم حرفه‌ای</h2>
            <p>در داده پردازان، موفقیت ما نتیجه تلاش جمعی، همدلی و تعهد تیمی است. تیمی که با تجربه، دانش و انگیزه، در مسیر رشد و پیشرفت قدم برمی‌دارد.</p>
          </div>
        </article>

        <article class="about-story-card about-story-card--workspace">
          <div class="about-story-copy">
            <span class="about-story-icon"><VsxIcon iconName="Building" :size="25" color="currentColor" type="linear" /></span>
            <h2>محیطی پویا و حرفه‌ای</h2>
            <p>داده پردازان، جایی است که تخصص و همکاری در کنار هم، فرصت‌های تازه‌ای برای رشد و یادگیری ایجاد می‌کنند.</p>
          </div>
          <div class="about-story-photo">
            <img src="/img/about4.jpg" alt="تیم داده‌پردازان در فضای اداری و کنار میزهای کار" loading="lazy" />
          </div>
        </article>
      </section>

      <aside class="about-transparency-banner">
        <div class="about-speaker-placeholder" role="img" aria-label="جایگاه تصویر بلندگوی بنفش و نارنجی؛ تصویر اصلی هنوز ارائه نشده است">
          <span class="about-speaker-halo"></span>
          <span class="about-speaker-body"></span>
          <span class="about-speaker-cone"></span>
          <span class="about-speaker-handle"></span>
          <span class="about-speaker-wave about-speaker-wave--one"></span>
          <span class="about-speaker-wave about-speaker-wave--two"></span>
        </div>
        <div class="about-transparency-copy">
          <h2>شفافیت، بنیان‌ترین اصل راهنمای داده‌پردازان</h2>
          <p>برای کسب اطلاعات بیشتر و مشاوره می‌توانید از طریق راه‌های ارتباطی با ما در تماس باشید.</p>
        </div>
        <router-link class="about-transparency-link" :to="{ name: 'ContactPage' }">
          <span>ارتباط با ما</span>
          <VsxIcon iconName="ArrowLeft2" :size="19" color="currentColor" type="linear" />
        </router-link>
      </aside>
    </section>

    <section v-else-if="pageKey === 'faq'" class="content-section faq-content">
      <h2>پاسخ پرسش‌های رایج</h2>
      <div class="faq-list">
        <article v-for="(item, index) in faqs" :key="item.question" class="faq-item" :class="{ 'is-open': openFaqIndex === index }">
          <h3 class="faq-question-heading">
            <button
              class="faq-question"
              type="button"
              :aria-expanded="openFaqIndex === index"
              :aria-controls="`faq-answer-${index}`"
              @click="toggleFaq(index)"
            >
              <span class="faq-question-text">{{ item.question }}</span>
              <span class="faq-toggle" aria-hidden="true"></span>
            </button>
          </h3>
          <div :id="`faq-answer-${index}`" class="faq-answer-grid" :class="{ 'is-open': openFaqIndex === index }" role="region" :aria-hidden="openFaqIndex !== index">
            <div class="faq-answer-inner">
              <p>{{ item.answer }}</p>
            </div>
          </div>
        </article>
      </div>

      <aside class="faq-support-card">
        <div class="faq-support-copy">
          <span class="faq-support-kicker">پشتیبانی داده‌پردازان</span>
          <h2>پاسخ سؤالاتتان را پیدا نکردید؟</h2>
          <p>تیم پشتیبانی آماده پاسخ‌گویی به سؤالات و مشکلات شماست.</p>
          <router-link class="faq-support-link" :to="{ name: 'ContactPage' }">
            ارتباط با پشتیبانی
            <VsxIcon iconName="ArrowLeft2" :size="18" color="currentColor" type="linear" />
          </router-link>
        </div>
        <div class="faq-support-art" aria-hidden="true">
          <span class="faq-support-halo"></span>
          <span class="faq-support-bubble faq-support-bubble--large"><VsxIcon iconName="Messages1" :size="24" color="#fff" type="linear" /></span>
          <span class="faq-support-bubble faq-support-bubble--small"><VsxIcon iconName="MessageText1" :size="18" color="#fff" type="linear" /></span>
          <span class="faq-support-headset"></span>
          <span class="faq-support-face"></span>
          <span class="faq-support-body"></span>
          <span class="faq-support-earpiece"></span>
          <span class="faq-support-mic"></span>
          <span class="faq-support-spark"></span>
        </div>
      </aside>
    </section>

    <section v-else-if="pageKey === 'journey'" class="content-section journey-content">
      <h2>مراحل آشنایی و استفاده از پنل</h2>
      <ol>
        <li v-for="(step, index) in journeySteps" :key="step.title">
          <span class="step-number">{{ String(index + 1).padStart(2, '0') }}</span>
          <div>
            <h3>{{ step.title }}</h3>
            <p>{{ step.description }}</p>
          </div>
        </li>
      </ol>
      <p class="notice">همیشه جزئیات و وضعیت‌هایی را که در پنل کاربری نمایش داده می‌شود، پیش از ثبت درخواست بررسی کنید.</p>
    </section>

    <section v-else class="content-section contact-experience">
      <div class="contact-actions" aria-label="راه‌های ارتباط با پشتیبانی">
        <a
          v-if="phoneHref"
          class="contact-action contact-action--phone"
          :href="phoneHref"
          :aria-label="`تماس با شماره ${siteSettings.site_phone}`"
        >
          <span class="action-icon action-icon--phone"><VsxIcon iconName="Call" :size="27" color="#fff" type="linear" /></span>
          <span class="action-copy">
            <small>شماره تماس</small>
            <strong dir="ltr">{{ siteSettings.site_phone }}</strong>
          </span>
          <VsxIcon class="action-arrow" iconName="ArrowLeft2" :size="20" color="currentColor" type="linear" />
        </a>
        <div v-else class="contact-action contact-action--phone contact-action--unavailable" aria-disabled="true">
          <span class="action-icon action-icon--phone"><VsxIcon iconName="Call" :size="27" color="#fff" type="linear" /></span>
          <span class="action-copy">
            <small>شماره تماس</small>
            <strong>شماره رسمی ثبت نشده است</strong>
          </span>
        </div>

        <div class="contact-action-grid">
          <a
            v-if="whatsappHref"
            class="contact-action contact-action--whatsapp"
            :href="whatsappHref"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="گفت‌وگو با پشتیبانی در واتساپ"
          >
            <span class="action-icon action-icon--whatsapp" aria-hidden="true">
              <svg viewBox="0 0 32 32" role="img" aria-label="واتساپ">
                <path d="M16 3.2A12.5 12.5 0 0 0 5.2 22l-1.5 5.5 5.7-1.5A12.5 12.5 0 1 0 16 3.2Z" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linejoin="round" />
                <path d="M12.2 10.5c-.3-.7-.6-.7-.9-.7h-.8c-.3 0-.8.1-1.1.5-.4.4-1.4 1.3-1.4 3.2s1.4 3.7 1.6 3.9c.2.3 2.7 4.2 6.6 5.7 3.3 1.3 4 .9 4.7.8.7-.1 2.2-.9 2.5-1.8.3-.9.3-1.6.2-1.8-.1-.2-.4-.3-.8-.5l-2.5-1.2c-.3-.1-.6-.2-.8.2-.2.4-1 1.2-1.2 1.5-.2.2-.4.3-.8.1-.4-.2-1.6-.6-3-1.9-1.1-1-1.9-2.2-2.1-2.6-.2-.4 0-.6.2-.8.2-.2.4-.5.6-.7.2-.2.3-.4.4-.6.1-.2.1-.5 0-.7l-1.1-2.6Z" fill="currentColor" />
              </svg>
            </span>
            <span class="action-copy">
              <strong>واتساپ</strong>
              <small>گفت‌وگو با پشتیبانی</small>
            </span>
            <VsxIcon class="action-arrow" iconName="ArrowLeft2" :size="20" color="currentColor" type="linear" />
          </a>
          <div v-else class="contact-action contact-action--whatsapp contact-action--unavailable" aria-disabled="true">
            <span class="action-icon action-icon--whatsapp" aria-hidden="true">
              <svg viewBox="0 0 32 32" role="img" aria-label="واتساپ">
                <path d="M16 3.2A12.5 12.5 0 0 0 5.2 22l-1.5 5.5 5.7-1.5A12.5 12.5 0 1 0 16 3.2Z" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linejoin="round" />
                <path d="M12.2 10.5c-.3-.7-.6-.7-.9-.7h-.8c-.3 0-.8.1-1.1.5-.4.4-1.4 1.3-1.4 3.2s1.4 3.7 1.6 3.9c.2.3 2.7 4.2 6.6 5.7 3.3 1.3 4 .9 4.7.8.7-.1 2.2-.9 2.5-1.8.3-.9.3-1.6.2-1.8-.1-.2-.4-.3-.8-.5l-2.5-1.2c-.3-.1-.6-.2-.8.2-.2.4-1 1.2-1.2 1.5-.2.2-.4.3-.8.1-.4-.2-1.6-.6-3-1.9-1.1-1-1.9-2.2-2.1-2.6-.2-.4 0-.6.2-.8.2-.2.4-.5.6-.7.2-.2.3-.4.4-.6.1-.2.1-.5 0-.7l-1.1-2.6Z" fill="currentColor" />
              </svg>
            </span>
            <span class="action-copy">
              <strong>واتساپ</strong>
              <small>شماره رسمی ثبت نشده است</small>
            </span>
          </div>

          <router-link class="contact-action contact-action--ticket" :to="{ name: 'ticketUser' }" aria-label="رفتن به ثبت تیکت پشتیبانی">
            <span class="action-icon action-icon--ticket"><VsxIcon iconName="Ticket" :size="27" color="#fff" type="linear" /></span>
            <span class="action-copy">
              <strong>ثبت تیکت</strong>
              <small>پیگیری درخواست شما</small>
            </span>
            <VsxIcon class="action-arrow" iconName="ArrowLeft2" :size="20" color="currentColor" type="linear" />
          </router-link>
        </div>
      </div>

      <div class="support-note">
        <div class="support-art" aria-hidden="true">
          <span class="art-orbit art-orbit--one"></span>
          <span class="art-orbit art-orbit--two"></span>
          <span class="art-bubble art-bubble--one"><VsxIcon iconName="Messages1" :size="24" color="#fff" type="linear" /></span>
          <span class="art-bubble art-bubble--two"><VsxIcon iconName="MessageText1" :size="19" color="#fff" type="linear" /></span>
          <span class="art-headset"></span>
          <span class="art-head"></span>
          <span class="art-body"></span>
          <span class="art-earpiece"></span>
          <span class="art-microphone"></span>
          <span class="art-spark art-spark--one"></span>
          <span class="art-spark art-spark--two"></span>
        </div>
        <div class="support-note-copy">
          <h2>همراه شما در مسیر پاسخ‌گویی</h2>
          <span class="support-note-line" aria-hidden="true"></span>
          <p>برای دریافت مشاوره و بررسی مشکلات خود، از راه‌های ارتباطی بالا با ما در تماس باشید.</p>
        </div>
      </div>
    </section>

    <footer class="page-footer">
      <span>© {{ new Date().getFullYear() }} {{ siteName }}</span>
      <router-link :to="{ name: 'HomePage' }">صفحه اصلی</router-link>
    </footer>
  </main>
</template>

<script>
import axios from '@/axios';

const pages = {
  ContactPage: {
    key: 'contact',
    eyebrow: 'ارتباط مستقیم',
    title: 'پشتیبانی و ارتباط با ما',
    intro: 'برای دریافت مشاوره و بررسی مشکلات خود، از راه‌های ارتباطی زیر با ما در تماس باشید.',
    icon: 'Call'
  },
  AboutPage: {
    key: 'about',
    eyebrow: 'درباره ما',
    title: 'داده پردازان',
    intro: 'با رویکردی شفاف و اصولی، با خدمات داده‌پردازان بیشتر آشنا شوید.',
    icon: 'Book'
  },
  FaqPage: {
    key: 'faq',
    eyebrow: 'راهنمای کاربران',
    title: 'پرسش‌های متداول',
    intro: 'پاسخ پرسش‌های رایج درباره ساخت حساب، احراز هویت و استفاده از پنل را اینجا ببینید.',
    icon: 'MessageQuestion'
  },
  JourneyPage: {
    key: 'journey',
    eyebrow: 'شروع کار',
    title: 'مسیر داده‌پردازان',
    intro: 'مراحل استفاده از پنل را قدم‌به‌قدم دنبال کنید و پیش از ثبت هر درخواست، شرایط آن را بررسی کنید.',
    icon: 'Security'
  }
};

export default {
  name: 'LandingInfoPage',
  data() {
    return {
      siteSettings: {
        site_name: 'داده‌پردازان',
        site_logo: null,
        site_email: '',
        site_phone: '021 9130 4972',
        site_whatsapp: '+98 21 9130 4972',
        about_us: 'داده‌پردازان خدمات خود را از طریق پنل آنلاین در اختیار کاربران قرار می‌دهد.',
        our_mission: 'هدف ما فراهم‌کردن تجربه‌ای روشن و قابل پیگیری برای استفاده از خدمات پنل است.',
        our_values: []
      },
      faqs: [
        {
          question: 'چطور حساب کاربری بسازم؟',
          answer: 'از صفحه اصلی وارد «ثبت‌نام» شوید و نام، نام کاربری، ایمیل، شماره موبایل و گذرواژه را همراه با تکرار آن وارد کنید. سپس دریافت کد را با ایمیل یا موبایل انتخاب کنید، کد تأیید را وارد کنید تا حساب ساخته شود. برای استفاده از بخش‌های پنل، احراز هویت هم لازم است.'
        },
        {
          question: 'وضعیت احراز هویت را از کجا پیگیری کنم؟',
          answer: 'پس از ورود، از «پروفایل» بخش «وضعیت احراز هویت» را ببینید. وضعیت‌ها شامل احراز هویت نشده، در حال بررسی، تأیید شده و رد شده است. اگر درخواست رد شده باشد، از صفحه احراز هویت اطلاعات را اصلاح و دوباره ارسال کنید.'
        },
        {
          question: 'چطور درخواست پشتیبانی ثبت کنم؟',
          answer: 'پس از ورود و تکمیل احراز هویت، از بخش «پشتیبانی» در پنل، ایجاد تیکت را انتخاب کنید و موضوع، اولویت و پیام را بفرستید. تیکت‌های ثبت‌شده و پاسخ‌ها از همان بخش قابل پیگیری هستند.'
        },
        {
          question: 'قراردادهای سرمایه‌گذاری را از کجا دریافت کنم؟',
          answer: 'پس از ورود، از داشبورد یا پروفایل وارد «قراردادها» شوید. قراردادهای ثبت‌شده در این بخش فهرست می‌شوند و برای هر موردِ موجود می‌توانید فایل PDF را دانلود کنید.'
        },
        {
          question: 'کجا وضعیت تراکنش‌ها و موجودی را ببینم؟',
          answer: 'موجودی قابل‌استفاده هر ارز را در موجودی داشبورد ببینید؛ بخش «کیف پول» جزئیات کیف پول‌ها را نشان می‌دهد. برای سوابق و وضعیت واریزها و برداشت‌ها، بخش «تراکنش‌ها» را باز کنید.'
        }
      ],
      legalDocuments: [
        { title: 'پروانه کسب', image: '/img/about2.jpg' },
        { title: 'گواهی‌نامه صلاحیت خدمات انفورماتیکی', image: '/img/about1.jpg' }
      ],
      openFaqIndex: null,
      journeySteps: [
        {
          title: 'ساخت حساب',
          description: 'از صفحه اصلی ثبت‌نام کنید و راه ورود امن به حساب خود را نگه دارید.'
        },
        {
          title: 'تکمیل اطلاعات حساب',
          description: 'در صورت نیاز، مراحل احراز هویت نمایش‌داده‌شده در پنل را تکمیل کنید.'
        },
        {
          title: 'آشنایی با خدمات',
          description: 'صفحه هر خدمت و شرایط پلن‌های قابل‌دسترسی را با دقت مطالعه کنید.'
        },
        {
          title: 'پیگیری از پنل',
          description: 'موجودی، تراکنش‌ها و پاسخ درخواست‌های پشتیبانی را از بخش‌های مربوط دنبال کنید.'
        }
      ]
    };
  },
  computed: {
    page() {
      return pages[this.$route.name] || pages.ContactPage;
    },
    pageKey() {
      return this.page.key;
    },
    siteName() {
      return this.siteSettings.site_name || 'داده‌پردازان';
    },
    phoneHref() {
      const phone = String(this.siteSettings.site_phone || '').trim();
      if (!phone) return '';
      const normalized = phone.replace(/(?!^\+)\D/g, '');
      return normalized ? `tel:${normalized}` : '';
    },
    whatsappHref() {
      const whatsapp = String(this.siteSettings.site_whatsapp || '').trim();
      if (!whatsapp) return '';
      let number = whatsapp.replace(/\D/g, '');
      if (number.startsWith('00')) number = number.slice(2);
      if (number.startsWith('0')) number = `98${number.slice(1)}`;
      if (number.length < 8 || number.length > 15) return '';
      return `https://wa.me/${number}?text=${encodeURIComponent('سلام، برای دریافت پشتیبانی راهنمایی می‌خواهم.')}`;
    },
    theme() {
      return this.$store.state.theme;
    },
    values() {
      return Array.isArray(this.siteSettings.our_values) && this.siteSettings.our_values.length
        ? this.siteSettings.our_values
        : ['شفافیت در اطلاع‌رسانی', 'توجه به امنیت حساب', 'پشتیبانی و پاسخ‌گویی', 'بهبود مستمر تجربه کاربران'];
    }
  },
  mounted() {
    document.body.style.overflow = 'auto';
    document.documentElement.style.overflow = 'auto';
    this.loadSettings();
  },
  beforeUnmount() {
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
  },
  methods: {
    toggleFaq(index) {
      this.openFaqIndex = this.openFaqIndex === index ? null : index;
    },
    async goToSoftware() {
      let user = this.$store.state.user;

      if (!user) {
        try {
          const storedUser = localStorage.getItem('user');
          user = storedUser ? JSON.parse(storedUser) : null;
        } catch (error) {
          user = null;
        }

        if (user) {
          this.$store.commit('setUser', user);
        }
      }

      await this.$router.push(user ? { name: 'IndexUser' } : { name: 'LoginUser' });
    },
    async loadSettings() {
      try {
        const { data } = await axios.get('/settings/public');
        if (!data?.success || !data.data) return;
        const settings = data.data;
        let values = [];
        try {
          values = Array.isArray(settings.our_values)
            ? settings.our_values
            : JSON.parse(settings.our_values || '[]');
        } catch (error) {
          values = [];
        }
        this.siteSettings = {
          ...this.siteSettings,
          site_name: settings.site_name || this.siteSettings.site_name,
          site_logo: settings.site_logo || null,
          site_email: settings.site_email || '',
          site_phone: settings.site_phone || this.siteSettings.site_phone,
          site_whatsapp: settings.site_whatsapp || this.siteSettings.site_whatsapp,
          about_us: settings.about_us || this.siteSettings.about_us,
          our_mission: settings.our_mission || this.siteSettings.our_mission,
          our_values: values
        };
      } catch (error) {
        console.warn('Unable to load public contact and about details.', error);
      }
    }
  }
};
</script>

<style scoped>
.public-info-page {
  --page-bg: #f7f4ff;
  --panel-bg: rgba(255, 255, 255, 0.8);
  --text: #302b3b;
  --muted: #686273;
  --accent: #3c278e;
  min-height: 100vh;
  padding: 0 clamp(20px, 6vw, 88px);
  background: radial-gradient(ellipse at 88% 10%, rgba(187, 134, 252, 0.2), transparent 36%), var(--page-bg);
  color: var(--text);
  font-family: 'iranyekanB', sans-serif;
}

.public-info-page.dark {
  --page-bg: #12121c;
  --panel-bg: rgba(31, 29, 43, 0.88);
  --text: #f5f2ff;
  --muted: #c1bbce;
  --accent: #c7a5f4;
}

.public-header {
  min-height: 82px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  border-bottom: 1px solid rgba(110, 91, 143, 0.16);
}

.brand-link,
.back-link,
.page-nav,
.page-nav a {
  display: flex;
  align-items: center;
}

.brand-link {
  gap: 10px;
  color: var(--text);
  font-size: 1.08rem;
  font-weight: 700;
  text-decoration: none;
  white-space: nowrap;
}

.page-nav {
  gap: clamp(12px, 2.2vw, 30px);
}

.page-nav a,
.back-link,
.page-footer a {
  color: var(--muted);
  text-decoration: none;
  transition: color 160ms ease;
}

.page-nav a:hover,
.page-nav a.router-link-active,
.back-link:hover,
.page-footer a:hover {
  color: var(--accent);
}

.back-link {
  gap: 7px;
  white-space: nowrap;
  padding: 0;
  border: 0;
  background: transparent;
  font: inherit;
  cursor: pointer;
}

.page-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 32px;
  max-width: 1100px;
  margin: 0 auto;
  padding: clamp(58px, 9vw, 112px) 0 60px;
}

.hero-copy {
  max-width: 760px;
}

.eyebrow {
  color: var(--accent);
  font-size: 0.9rem;
  font-weight: 700;
}

.page-hero h1 {
  margin: 13px 0 15px;
  font-size: clamp(2.4rem, 5vw, 4rem);
  line-height: 1.25;
  letter-spacing: -0.035em;
}

.page-hero p,
.content-section p {
  color: var(--muted);
  line-height: 2;
}

.page-hero p {
  max-width: 62ch;
  margin: 0;
  font-size: 1.08rem;
}

.hero-mark {
  width: 112px;
  height: 112px;
  flex: 0 0 112px;
  display: grid;
  place-items: center;
  border: 1px solid rgba(60, 39, 142, 0.15);
  border-radius: 30px;
  background: var(--panel-bg);
  box-shadow: 0 18px 48px rgba(40, 28, 72, 0.08);
}

.dark .hero-mark :deep(svg) {
  color: #c7a5f4 !important;
}

.content-section {
  max-width: 1100px;
  margin: 0 auto;
  padding: 34px 0 110px;
}

.content-section h2 {
  margin: 0 0 22px;
  font-size: clamp(1.35rem, 2.4vw, 1.8rem);
}

.about-layout {
  display: grid;
  grid-template-columns: 1.25fr 0.75fr;
  gap: clamp(35px, 7vw, 90px);
}

.copy-block p {
  margin: 0 0 18px;
}

.values-panel {
  padding: 28px;
  border-radius: 22px;
  background: var(--panel-bg);
  box-shadow: 0 18px 48px rgba(40, 28, 72, 0.06);
}

.values-panel ul {
  display: grid;
  gap: 17px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.values-panel li {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  line-height: 1.8;
}

.faq-content {
  max-width: 850px;
}

.faq-content details {
  padding: 19px 2px;
  border-bottom: 1px solid rgba(110, 91, 143, 0.2);
}

.faq-content summary {
  cursor: pointer;
  font-weight: 700;
  line-height: 1.8;
}

.faq-content summary::marker {
  color: var(--accent);
}

.faq-content details p {
  margin: 12px 0 0;
}

.journey-content ol {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 22px 42px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.journey-content li {
  display: flex;
  align-items: flex-start;
  gap: 17px;
  padding: 22px;
  border-radius: 18px;
  background: var(--panel-bg);
}

.step-number {
  color: var(--accent);
  font-size: 1.2rem;
  font-weight: 700;
}

.journey-content h3 {
  margin: 0 0 8px;
  font-size: 1.12rem;
}

.journey-content li p {
  margin: 0;
}

.notice {
  margin-top: 26px;
  padding: 15px 18px;
  border-radius: 12px;
  background: rgba(187, 134, 252, 0.12);
}

.contact-layout {
  display: grid;
  grid-template-columns: 1fr 0.8fr;
  align-items: center;
  gap: 50px;
}

.contact-copy p {
  max-width: 58ch;
}

.support-button {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin-top: 15px;
  padding: 13px 20px;
  border-radius: 12px;
  background: #3c278e;
  color: #fff;
  text-decoration: none;
}

.contact-details {
  display: grid;
  gap: 15px;
}

.contact-details a {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 18px;
  border-radius: 16px;
  background: var(--panel-bg);
  color: var(--text);
  text-decoration: none;
  overflow-wrap: anywhere;
}

.contact-details a > span:last-child {
  display: grid;
  gap: 5px;
}

.contact-details small {
  color: var(--muted);
}

.contact-icon {
  width: 46px;
  height: 46px;
  display: grid;
  place-items: center;
  border-radius: 13px;
  background: rgba(60, 39, 142, 0.1);
}

.dark .contact-icon :deep(svg) {
  color: #c7a5f4 !important;
}

.page-footer {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 22px 0;
  border-top: 1px solid rgba(110, 91, 143, 0.16);
  color: var(--muted);
  font-size: 0.9rem;
}

@media (max-width: 760px) {
  .public-header {
    flex-wrap: wrap;
    padding: 16px 0;
  }

  .page-nav {
    order: 3;
    width: 100%;
    overflow-x: auto;
    padding: 4px 0;
    white-space: nowrap;
  }

  .page-hero {
    padding-top: 58px;
  }

  .hero-mark {
    width: 82px;
    height: 82px;
    flex-basis: 82px;
    border-radius: 24px;
  }

  .about-layout,
  .contact-layout {
    grid-template-columns: 1fr;
  }

  .journey-content ol {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .public-info-page {
    padding: 0 18px;
  }

  .page-hero {
    align-items: flex-start;
    gap: 15px;
  }

  .hero-mark {
    width: 60px;
    height: 60px;
    flex-basis: 60px;
    border-radius: 18px;
  }

  .hero-mark :deep(svg) {
    width: 32px;
  }

  .page-hero h1 {
    font-size: 2rem;
  }

  .back-link {
    font-size: 0.86rem;
  }
}

.brand-logo {
  width: 34px;
  height: 34px;
  object-fit: contain;
}

.contact-page {
  position: relative;
  isolation: isolate;
  overflow: hidden;
  background:
    radial-gradient(ellipse at 89% 12%, rgba(165, 136, 246, 0.2), transparent 30%),
    radial-gradient(ellipse at 4% 65%, rgba(255, 177, 126, 0.12), transparent 27%),
    #fbfaff;
}

.contact-page::before,
.contact-page::after {
  position: absolute;
  z-index: -1;
  content: '';
  pointer-events: none;
}

.contact-page::before {
  top: 154px;
  right: max(2%, calc((100vw - 1360px) / 2));
  width: 18px;
  height: 18px;
  border: 3px solid rgba(255, 157, 93, 0.52);
  border-radius: 5px;
  transform: rotate(24deg);
}

.contact-page::after {
  bottom: 130px;
  left: max(3%, calc((100vw - 1320px) / 2));
  width: 34px;
  height: 34px;
  border: 1px solid rgba(91, 70, 179, 0.2);
  border-radius: 50%;
  box-shadow: 12px -13px 0 -8px rgba(91, 70, 179, 0.45);
}

.contact-page.dark {
  background:
    radial-gradient(ellipse at 89% 12%, rgba(111, 74, 179, 0.24), transparent 30%),
    radial-gradient(ellipse at 4% 65%, rgba(171, 92, 63, 0.12), transparent 27%),
    #12121c;
}

.contact-page .public-header,
.contact-page .page-footer {
  position: relative;
  z-index: 1;
}

.contact-page .page-hero {
  max-width: 920px;
  padding: clamp(50px, 7vw, 78px) 0 22px;
}

.contact-page .eyebrow {
  display: none;
}

.contact-page .hero-copy {
  max-width: 790px;
}

.contact-page .contact-title {
  display: flex;
  flex-wrap: wrap;
  gap: 0.18em 0.32em;
  margin: 0 0 15px;
  font-size: clamp(2.15rem, 5vw, 3.55rem);
  line-height: 1.35;
  letter-spacing: -0.035em;
}

.contact-title-accent {
  color: #e87538;
}

.contact-page .page-hero p {
  max-width: 62ch;
  font-size: clamp(0.98rem, 1.6vw, 1.1rem);
}

.contact-experience {
  max-width: 920px;
  padding: 20px 0 54px;
}

.contact-actions {
  display: grid;
  gap: 17px;
}

.contact-action-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 17px;
}

.contact-action {
  position: relative;
  display: flex;
  min-width: 0;
  min-height: 106px;
  align-items: center;
  gap: 18px;
  padding: 20px 24px;
  overflow: hidden;
  border: 1px solid rgba(91, 70, 179, 0.14);
  border-radius: 25px;
  background: rgba(255, 255, 255, 0.92);
  color: var(--text);
  text-decoration: none;
  box-shadow: 0 15px 38px rgba(57, 40, 111, 0.09), 0 3px 8px rgba(57, 40, 111, 0.035);
  transition: transform 220ms cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 220ms ease, border-color 220ms ease;
}

.contact-action::after {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  height: 3px;
  content: '';
  opacity: 0.8;
  transform: scaleX(0.2);
  transform-origin: right;
  transition: transform 220ms ease;
}

.contact-action--phone {
  min-height: 125px;
  border-color: rgba(110, 82, 196, 0.2);
  background: linear-gradient(112deg, rgba(255, 255, 255, 0.97), rgba(248, 245, 255, 0.97));
  box-shadow: 0 20px 46px rgba(74, 53, 145, 0.13), 0 4px 12px rgba(74, 53, 145, 0.06);
}

.contact-action--phone::after {
  background: linear-gradient(90deg, #f2a04d, #7354d4);
}

.contact-action--whatsapp {
  border-color: rgba(35, 164, 103, 0.2);
  background: linear-gradient(125deg, rgba(241, 255, 248, 0.98), rgba(255, 255, 255, 0.95));
}

.contact-action--whatsapp::after {
  background: linear-gradient(90deg, #45c786, #178d5a);
}

.contact-action--ticket {
  border-color: rgba(112, 83, 207, 0.21);
  background: linear-gradient(125deg, rgba(246, 243, 255, 0.98), rgba(255, 255, 255, 0.96));
}

.contact-action--ticket::after {
  background: linear-gradient(90deg, #a58af1, #5439ac);
}

.contact-action[href]:hover {
  transform: translateY(-4px);
  box-shadow: 0 23px 48px rgba(57, 40, 111, 0.16), 0 7px 15px rgba(57, 40, 111, 0.07);
}

.contact-action[href]:hover::after {
  transform: scaleX(1);
}

.contact-action[href]:active {
  transform: translateY(-1px) scale(0.99);
}

.contact-action:focus-visible,
.contact-page .brand-link:focus-visible,
.contact-page .page-nav a:focus-visible,
.contact-page .back-link:focus-visible,
.contact-page .page-footer a:focus-visible {
  outline: 3px solid #9278e7;
  outline-offset: 4px;
}

.contact-action--unavailable {
  cursor: not-allowed;
  border-style: dashed;
  box-shadow: 0 10px 24px rgba(57, 40, 111, 0.055);
}

.contact-action--unavailable::after {
  opacity: 0.35;
  transform: scaleX(1);
}

.action-icon {
  display: grid;
  width: 58px;
  height: 58px;
  flex: 0 0 58px;
  place-items: center;
  border-radius: 19px;
  color: white;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.3), 0 8px 17px rgba(45, 35, 84, 0.2);
}

.action-icon--phone {
  background: linear-gradient(145deg, #9a7be9, #4b329f);
  box-shadow: inset 0 2px 2px rgba(255, 255, 255, 0.3), 0 9px 18px rgba(79, 53, 157, 0.28);
}

.action-icon--whatsapp {
  background: linear-gradient(145deg, #57d692, #19965d);
  box-shadow: inset 0 2px 2px rgba(255, 255, 255, 0.3), 0 8px 16px rgba(25, 150, 93, 0.2);
}

.action-icon--whatsapp svg {
  width: 30px;
  height: 30px;
}

.action-icon--ticket {
  background: linear-gradient(145deg, #9c82ed, #5739b1);
  box-shadow: inset 0 2px 2px rgba(255, 255, 255, 0.3), 0 8px 16px rgba(87, 57, 177, 0.23);
}

.action-copy {
  display: grid;
  min-width: 0;
  gap: 7px;
}

.action-copy small {
  color: var(--muted);
  font-size: 0.86rem;
}

.action-copy strong {
  color: var(--text);
  font-size: clamp(1rem, 2vw, 1.28rem);
  font-weight: 700;
  overflow-wrap: anywhere;
}

.contact-action--phone .action-copy strong {
  color: #432d91;
  font-size: clamp(1.2rem, 2.7vw, 1.65rem);
  font-variant-numeric: tabular-nums;
  unicode-bidi: isolate;
}

.action-arrow {
  flex: 0 0 auto;
  margin-right: auto;
  opacity: 0.58;
}

.contact-action--whatsapp .action-arrow {
  color: #168750 !important;
}

.contact-action--ticket .action-arrow {
  color: #583eb0 !important;
}

.support-note {
  display: grid;
  grid-template-columns: minmax(200px, 0.75fr) minmax(0, 1.25fr);
  align-items: center;
  gap: clamp(28px, 7vw, 80px);
  min-height: 240px;
  margin-top: 34px;
  padding: 8px clamp(10px, 4vw, 40px);
}

.support-art {
  position: relative;
  width: min(100%, 270px);
  height: 218px;
  justify-self: center;
  filter: drop-shadow(0 18px 17px rgba(60, 39, 142, 0.12));
}

.art-orbit {
  position: absolute;
  inset: 23px 22px 10px;
  border: 1px solid rgba(124, 97, 209, 0.16);
  border-radius: 48% 52% 48% 52%;
  transform: rotate(-18deg);
}

.art-orbit--two {
  inset: 43px 4px 29px;
  border-color: rgba(237, 141, 85, 0.22);
  transform: rotate(23deg);
}

.art-head {
  position: absolute;
  z-index: 2;
  top: 57px;
  left: 100px;
  width: 70px;
  height: 81px;
  border: 3px solid rgba(255, 255, 255, 0.52);
  border-radius: 45% 45% 42% 42%;
  background: linear-gradient(135deg, #ffd3b2 0%, #f5a77e 72%, #dd805e 100%);
  box-shadow: inset -7px -4px 12px rgba(151, 68, 73, 0.14), 0 8px 17px rgba(92, 58, 117, 0.16);
}

.art-headset {
  position: absolute;
  z-index: 3;
  top: 35px;
  left: 91px;
  width: 88px;
  height: 85px;
  border: 10px solid transparent;
  border-top-color: #443085;
  border-right-color: #443085;
  border-left-color: #443085;
  border-radius: 50% 50% 0 0;
  filter: drop-shadow(2px 3px 2px rgba(49, 32, 104, 0.2));
}

.art-body {
  position: absolute;
  z-index: 1;
  bottom: 7px;
  left: 63px;
  width: 146px;
  height: 98px;
  border: 3px solid rgba(255, 255, 255, 0.46);
  border-radius: 72px 72px 34px 34px;
  background: linear-gradient(145deg, #8871da 0%, #604ab5 64%, #3e2a8b 100%);
  box-shadow: inset -10px -8px 15px rgba(45, 30, 107, 0.2), 0 12px 19px rgba(57, 40, 111, 0.2);
}

.art-earpiece {
  position: absolute;
  z-index: 4;
  top: 75px;
  left: 85px;
  width: 22px;
  height: 43px;
  border: 3px solid #f8f5ff;
  border-radius: 11px;
  background: linear-gradient(135deg, #765bd0, #423082);
  box-shadow: 0 4px 9px rgba(45, 30, 107, 0.2);
}

.art-microphone {
  position: absolute;
  z-index: 4;
  top: 108px;
  left: 85px;
  width: 51px;
  height: 29px;
  border-right: 4px solid #443085;
  border-bottom: 4px solid #443085;
  border-radius: 0 0 22px 0;
  transform: rotate(-13deg);
}

.art-microphone::after {
  position: absolute;
  right: -8px;
  bottom: -7px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #ed8a50;
  content: '';
}

.art-bubble {
  position: absolute;
  z-index: 5;
  display: grid;
  place-items: center;
  border: 3px solid rgba(255, 255, 255, 0.84);
  border-radius: 17px;
  box-shadow: 0 8px 16px rgba(54, 40, 105, 0.18);
}

.art-bubble--one {
  top: 16px;
  right: 16px;
  width: 55px;
  height: 47px;
  background: linear-gradient(145deg, #f6a05c, #e4763e);
  transform: rotate(7deg);
}

.art-bubble--two {
  top: 78px;
  right: 3px;
  width: 43px;
  height: 38px;
  border-radius: 14px;
  background: linear-gradient(145deg, #68b6ed, #438ed0);
  transform: rotate(-8deg);
}

.art-spark {
  position: absolute;
  width: 8px;
  height: 8px;
  border-radius: 2px;
  background: #ee9a5e;
  transform: rotate(45deg);
}

.art-spark--one {
  top: 49px;
  left: 41px;
}

.art-spark--two {
  right: 23px;
  bottom: 24px;
  width: 6px;
  height: 6px;
  background: #7d66cd;
}

.support-note-copy h2 {
  margin: 0;
  color: var(--text);
  font-size: clamp(1.45rem, 3vw, 2.15rem);
  line-height: 1.55;
}

.support-note-line {
  display: block;
  width: 82px;
  height: 4px;
  margin: 15px 0 13px;
  border-radius: 99px;
  background: linear-gradient(90deg, #f5a04e, #e8793e);
}

.support-note-copy p {
  max-width: 48ch;
  margin: 0;
  color: var(--muted);
  line-height: 2;
}

.contact-page.dark .contact-action {
  background-color: rgba(31, 29, 43, 0.95);
  color: var(--text);
}

.contact-page.dark .contact-action--phone {
  background-image: linear-gradient(112deg, rgba(38, 34, 56, 0.97), rgba(34, 29, 50, 0.97));
}

.contact-page.dark .contact-action--whatsapp {
  background-image: linear-gradient(125deg, rgba(25, 54, 43, 0.96), rgba(31, 29, 43, 0.96));
}

.contact-page.dark .contact-action--ticket {
  background-image: linear-gradient(125deg, rgba(48, 39, 76, 0.96), rgba(31, 29, 43, 0.96));
}

.contact-page.dark .contact-action--phone .action-copy strong {
  color: #d2c3ff;
}

.faq-page {
  position: relative;
  isolation: isolate;
  overflow: hidden;
  background:
    radial-gradient(ellipse at 9% 15%, rgba(178, 157, 240, 0.22), transparent 30%),
    radial-gradient(ellipse at 91% 60%, rgba(255, 186, 138, 0.13), transparent 28%),
    #fbfaff;
}

.faq-page::before,
.faq-page::after {
  position: absolute;
  z-index: -1;
  content: '';
  pointer-events: none;
}

.faq-page::before {
  top: 194px;
  left: max(3%, calc((100vw - 1360px) / 2));
  width: 18px;
  height: 18px;
  border: 3px solid rgba(235, 143, 83, 0.48);
  border-radius: 5px;
  transform: rotate(22deg);
}

.faq-page::after {
  right: max(3%, calc((100vw - 1320px) / 2));
  bottom: 180px;
  width: 31px;
  height: 31px;
  border: 1px solid rgba(100, 77, 177, 0.2);
  border-radius: 50%;
  box-shadow: -13px 12px 0 -8px rgba(235, 143, 83, 0.6);
}

.faq-page.dark {
  background:
    radial-gradient(ellipse at 9% 15%, rgba(103, 79, 170, 0.23), transparent 30%),
    radial-gradient(ellipse at 91% 60%, rgba(153, 91, 57, 0.12), transparent 28%),
    #12121c;
}

.faq-page .page-hero {
  max-width: 1080px;
  min-height: 310px;
  padding-top: clamp(43px, 6vw, 75px);
  padding-bottom: 26px;
}

.faq-page .hero-copy {
  max-width: 670px;
}

.faq-page .eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  padding: 7px 13px;
  border: 1px solid rgba(103, 77, 183, 0.12);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.66);
}

.faq-page .eyebrow::before {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #e98d51;
  content: '';
}

.faq-page .page-hero h1 {
  margin-top: 16px;
  color: #35236f;
  font-size: clamp(2.35rem, 5vw, 3.8rem);
}

.faq-page.dark .page-hero h1 {
  color: #e5dbff;
}

.faq-page .page-hero p {
  max-width: 56ch;
}

.faq-hero-art {
  position: relative;
  width: clamp(190px, 27vw, 270px);
  flex: 0 0 clamp(190px, 27vw, 270px);
  aspect-ratio: 260 / 220;
  isolation: isolate;
}

.faq-hero-art svg {
  position: relative;
  z-index: 1;
  display: block;
  width: 100%;
  height: 100%;
  overflow: visible;
}

.faq-art-orbit {
  position: absolute;
  z-index: -1;
  inset: 12% 8% 6%;
  border: 1px solid rgba(110, 84, 190, 0.13);
  border-radius: 48% 52% 45% 55%;
  transform: rotate(-18deg);
}

.faq-art-orbit--two {
  inset: 20% 0 15%;
  border-color: rgba(232, 139, 81, 0.18);
  transform: rotate(17deg);
}

.faq-content {
  max-width: 860px;
  padding-top: 19px;
  padding-bottom: 76px;
}

.faq-content > h2 {
  margin-bottom: 23px;
  color: var(--text);
  font-size: clamp(1.4rem, 2.4vw, 1.8rem);
}

.faq-list {
  display: grid;
  gap: 12px;
}

.faq-item {
  overflow: hidden;
  border: 1px solid rgba(118, 96, 186, 0.19);
  border-radius: 17px;
  background: rgba(255, 255, 255, 0.84);
  box-shadow: 0 7px 22px rgba(54, 39, 104, 0.045);
  transition: border-color 180ms ease, background-color 180ms ease, box-shadow 180ms ease;
}

.faq-item.is-open {
  border-color: rgba(116, 87, 192, 0.38);
  background: #fff;
  box-shadow: 0 13px 30px rgba(54, 39, 104, 0.085);
}

.faq-question-heading {
  margin: 0;
  font: inherit;
}

.faq-question {
  display: flex;
  width: 100%;
  min-height: 70px;
  align-items: center;
  gap: 18px;
  padding: 16px 20px;
  border: 0;
  background: transparent;
  color: var(--text);
  font: inherit;
  text-align: right;
  cursor: pointer;
}

.faq-question:focus-visible,
.faq-support-link:focus-visible {
  outline: 3px solid #9278e7;
  outline-offset: -4px;
}

.faq-question-text {
  flex: 1;
  font-weight: 700;
  line-height: 1.8;
}

.faq-toggle {
  position: relative;
  display: block;
  width: 35px;
  height: 35px;
  flex: 0 0 35px;
  border: 1px solid rgba(110, 83, 189, 0.2);
  border-radius: 12px;
  background: #f4f0ff;
  transition: background-color 180ms ease, transform 180ms ease, border-color 180ms ease;
}

.faq-toggle::before,
.faq-toggle::after {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 13px;
  height: 2px;
  border-radius: 2px;
  background: #5d43aa;
  content: '';
  transform: translate(-50%, -50%);
  transition: transform 200ms ease, opacity 150ms ease;
}

.faq-toggle::after {
  transform: translate(-50%, -50%) rotate(90deg);
}

.faq-item.is-open .faq-toggle {
  border-color: #7257c0;
  background: linear-gradient(145deg, #866cda, #5a40ad);
  transform: rotate(180deg);
}

.faq-item.is-open .faq-toggle::before,
.faq-item.is-open .faq-toggle::after {
  background: white;
}

.faq-item.is-open .faq-toggle::after {
  opacity: 0;
  transform: translate(-50%, -50%) rotate(0);
}

.faq-answer-grid {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 280ms cubic-bezier(0.2, 0.75, 0.25, 1);
}

.faq-answer-grid.is-open {
  grid-template-rows: 1fr;
}

.faq-answer-inner {
  min-height: 0;
  overflow: hidden;
}

.faq-answer-inner p {
  margin: 0;
  padding: 0 74px 18px 24px;
  color: var(--muted);
  line-height: 2;
  opacity: 0;
  transform: translateY(-5px);
  transition: opacity 180ms ease, transform 220ms ease;
}

.faq-answer-grid.is-open .faq-answer-inner p {
  opacity: 1;
  transform: translateY(0);
  transition-delay: 50ms;
}

.faq-support-card {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1.25fr) minmax(200px, 0.75fr);
  align-items: center;
  gap: 20px;
  min-height: 226px;
  margin-top: 55px;
  padding: 28px clamp(24px, 4vw, 44px);
  overflow: hidden;
  border: 1px solid rgba(126, 103, 194, 0.18);
  border-radius: 25px;
  background:
    radial-gradient(ellipse at 91% 8%, rgba(255, 255, 255, 0.7), transparent 42%),
    linear-gradient(115deg, #f0eaff 0%, #f5f0ff 54%, #eee8ff 100%);
  box-shadow: 0 17px 38px rgba(55, 39, 112, 0.09);
}

.faq-support-copy {
  position: relative;
  z-index: 2;
}

.faq-support-kicker {
  display: inline-block;
  margin-bottom: 8px;
  color: #7358b9;
  font-size: 0.84rem;
  font-weight: 700;
}

.faq-support-copy h2 {
  margin: 0 0 7px;
  color: #35236f;
  font-size: clamp(1.3rem, 2.5vw, 1.8rem);
  line-height: 1.55;
}

.faq-support-copy p {
  margin: 0 0 17px;
  color: #655c7a;
  line-height: 1.9;
}

.faq-support-link {
  display: inline-flex;
  min-height: 45px;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 9px 17px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 13px;
  background: linear-gradient(135deg, #7356c4, #503596);
  color: #fff;
  font-weight: 700;
  text-decoration: none;
  box-shadow: 0 7px 16px rgba(80, 53, 150, 0.2);
  transition: transform 180ms ease, box-shadow 180ms ease;
}

.faq-support-link:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 21px rgba(80, 53, 150, 0.26);
}

.faq-support-link:active {
  transform: translateY(0) scale(0.98);
}

.faq-support-art {
  position: relative;
  width: min(100%, 240px);
  height: 190px;
  justify-self: center;
  filter: drop-shadow(0 14px 13px rgba(59, 41, 119, 0.12));
}

.faq-support-halo {
  position: absolute;
  inset: 17px 13px 2px;
  border: 1px solid rgba(111, 87, 189, 0.15);
  border-radius: 47% 53% 51% 49%;
  transform: rotate(-13deg);
}

.faq-support-face {
  position: absolute;
  z-index: 2;
  top: 49px;
  left: 85px;
  width: 62px;
  height: 72px;
  border: 3px solid rgba(255, 255, 255, 0.58);
  border-radius: 46% 46% 42% 42%;
  background: linear-gradient(135deg, #ffd9bd, #f0a27a 78%, #dc805f);
  box-shadow: inset -6px -4px 10px rgba(145, 64, 70, 0.12), 0 7px 14px rgba(73, 51, 113, 0.15);
}

.faq-support-headset {
  position: absolute;
  z-index: 3;
  top: 30px;
  left: 76px;
  width: 81px;
  height: 75px;
  border: 9px solid transparent;
  border-top-color: #443085;
  border-right-color: #443085;
  border-left-color: #443085;
  border-radius: 50% 50% 0 0;
  filter: drop-shadow(2px 3px 2px rgba(49, 32, 104, 0.2));
}

.faq-support-body {
  position: absolute;
  z-index: 1;
  bottom: 5px;
  left: 50px;
  width: 132px;
  height: 82px;
  border: 3px solid rgba(255, 255, 255, 0.48);
  border-radius: 68px 68px 30px 30px;
  background: linear-gradient(145deg, #927be1, #6047b2 68%, #422d8f);
  box-shadow: inset -9px -7px 13px rgba(45, 30, 107, 0.18), 0 10px 17px rgba(57, 40, 111, 0.17);
}

.faq-support-earpiece {
  position: absolute;
  z-index: 4;
  top: 65px;
  left: 69px;
  width: 21px;
  height: 39px;
  border: 3px solid #faf8ff;
  border-radius: 11px;
  background: linear-gradient(135deg, #8068d5, #423082);
}

.faq-support-mic {
  position: absolute;
  z-index: 4;
  top: 96px;
  left: 70px;
  width: 46px;
  height: 26px;
  border-right: 4px solid #443085;
  border-bottom: 4px solid #443085;
  border-radius: 0 0 19px 0;
  transform: rotate(-11deg);
}

.faq-support-mic::after {
  position: absolute;
  right: -8px;
  bottom: -7px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #ed8a50;
  content: '';
}

.faq-support-bubble {
  position: absolute;
  z-index: 5;
  display: grid;
  place-items: center;
  border: 3px solid rgba(255, 255, 255, 0.85);
  border-radius: 15px;
  box-shadow: 0 7px 14px rgba(54, 40, 105, 0.18);
}

.faq-support-bubble--large {
  top: 12px;
  right: 14px;
  width: 51px;
  height: 44px;
  background: linear-gradient(145deg, #f6a05c, #e4763e);
  transform: rotate(7deg);
}

.faq-support-bubble--small {
  top: 68px;
  right: 3px;
  width: 40px;
  height: 35px;
  border-radius: 12px;
  background: linear-gradient(145deg, #68b6ed, #438ed0);
  transform: rotate(-8deg);
}

.faq-support-spark {
  position: absolute;
  top: 41px;
  left: 29px;
  width: 8px;
  height: 8px;
  border-radius: 2px;
  background: #ee9a5e;
  transform: rotate(45deg);
}

.faq-page.dark .faq-item {
  border-color: rgba(162, 139, 226, 0.25);
  background: rgba(31, 29, 43, 0.91);
}

.faq-page.dark .faq-item.is-open {
  background: rgba(38, 34, 54, 0.98);
}

.faq-page.dark .faq-toggle {
  border-color: rgba(178, 154, 237, 0.35);
  background: rgba(92, 70, 151, 0.34);
}

.faq-page.dark .faq-toggle::before,
.faq-page.dark .faq-toggle::after {
  background: #dfd2ff;
}

.faq-page.dark .faq-item.is-open .faq-toggle {
  background: linear-gradient(145deg, #866cda, #5a40ad);
}

.faq-page.dark .faq-item.is-open .faq-toggle::before,
.faq-page.dark .faq-item.is-open .faq-toggle::after {
  background: #fff;
}

.faq-page.dark .faq-support-card {
  border-color: rgba(162, 139, 226, 0.22);
  background:
    radial-gradient(ellipse at 91% 8%, rgba(132, 111, 190, 0.2), transparent 42%),
    linear-gradient(115deg, #242036 0%, #29233d 54%, #26203a 100%);
}

.faq-page.dark .faq-support-copy h2 {
  color: #e6dcff;
}

.faq-page.dark .faq-support-copy p,
.faq-page.dark .faq-support-kicker {
  color: #c9bedf;
}

.about-page {
  position: relative;
  isolation: isolate;
  overflow: hidden;
  background:
    radial-gradient(ellipse at 10% 10%, rgba(185, 166, 243, 0.2), transparent 30%),
    radial-gradient(ellipse at 91% 47%, rgba(255, 190, 144, 0.11), transparent 27%),
    #fbfaff;
}

.about-page::before,
.about-page::after {
  position: absolute;
  z-index: -1;
  content: '';
  pointer-events: none;
}

.about-page::before {
  top: 176px;
  left: max(3%, calc((100vw - 1360px) / 2));
  width: 15px;
  height: 15px;
  border: 2px solid rgba(234, 145, 90, 0.48);
  border-radius: 4px;
  transform: rotate(28deg);
}

.about-page::after {
  right: max(3%, calc((100vw - 1340px) / 2));
  bottom: 230px;
  width: 28px;
  height: 28px;
  border: 1px solid rgba(111, 88, 184, 0.2);
  border-radius: 50%;
  box-shadow: -12px 13px 0 -8px rgba(235, 143, 83, 0.6);
}

.about-page.dark {
  background:
    radial-gradient(ellipse at 10% 10%, rgba(103, 79, 170, 0.23), transparent 30%),
    radial-gradient(ellipse at 91% 47%, rgba(153, 91, 57, 0.12), transparent 27%),
    #12121c;
}

.about-hero {
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(330px, 0.95fr);
  align-items: center;
  gap: clamp(34px, 7vw, 92px);
  width: min(100%, 1100px);
  min-height: 345px;
  margin: 0 auto;
  padding: 35px 0 24px;
}

.about-hero-copy {
  max-width: 610px;
}

.about-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border: 1px solid rgba(109, 83, 184, 0.15);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.76);
  color: #5e45a6;
  font-size: 0.9rem;
  font-weight: 700;
}

.about-hero-copy h1 {
  margin: 18px 0 12px;
  color: #292449;
  font-size: clamp(2.8rem, 5vw, 4.35rem);
  font-weight: 800;
  letter-spacing: -0.04em;
  line-height: 1.25;
}

.about-hero-copy p {
  max-width: 48ch;
  margin: 0;
  color: #666177;
  font-size: clamp(1.02rem, 1.7vw, 1.17rem);
  line-height: 2;
}

.about-hero-visual {
  position: relative;
  display: grid;
  min-height: 286px;
  place-items: center;
  isolation: isolate;
}

.about-hero-visual::before {
  position: absolute;
  z-index: -2;
  inset: 18px 14px 20px;
  border: 1px solid rgba(119, 96, 190, 0.14);
  border-radius: 44% 56% 48% 52%;
  background: radial-gradient(ellipse at 53% 46%, rgba(255, 255, 255, 0.92), rgba(242, 237, 255, 0.48) 58%, transparent 74%);
  content: '';
  transform: rotate(-9deg);
}

.about-chart-grid {
  position: absolute;
  z-index: -1;
  inset: 51px 14px 35px;
  overflow: hidden;
  border: 1px solid rgba(113, 92, 179, 0.13);
  border-radius: 20px;
  background-image: linear-gradient(rgba(103, 84, 170, 0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(103, 84, 170, 0.07) 1px, transparent 1px);
  background-size: 35px 35px;
  opacity: 0.78;
  transform: perspective(460px) rotateY(-15deg) rotateX(4deg);
}

.about-chart-bar {
  position: absolute;
  bottom: 17px;
  width: 18px;
  border: 1px solid rgba(255, 255, 255, 0.76);
  border-radius: 5px 5px 2px 2px;
  background: linear-gradient(180deg, rgba(132, 110, 213, 0.6), rgba(103, 80, 178, 0.2));
}

.about-chart-bar--one { left: 8%; height: 33px; }
.about-chart-bar--two { left: 23%; height: 68px; }
.about-chart-bar--three { left: 39%; height: 48px; }
.about-chart-bar--four { left: 71%; height: 88px; }
.about-chart-bar--five { left: 86%; height: 115px; }

.about-chart-line {
  position: absolute;
  z-index: 1;
  top: 38%;
  left: 4%;
  width: 91%;
  height: 2px;
  border-radius: 99px;
  background: linear-gradient(90deg, transparent, #f1a05f 15%, #7559c0 52%, #5ca9c8 84%, transparent);
  transform: rotate(-17deg);
}

.about-chart-line--two {
  top: 65%;
  left: 16%;
  width: 73%;
  height: 1px;
  opacity: 0.5;
  transform: rotate(11deg);
}

.about-logo-plinth {
  position: relative;
  display: grid;
  width: clamp(154px, 19vw, 205px);
  aspect-ratio: 1;
  place-items: center;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.94);
  border-radius: 34px;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.96), rgba(242, 237, 255, 0.9));
  box-shadow: 0 23px 48px rgba(58, 43, 115, 0.18), inset 0 1px 1px #fff;
  transform: rotate(-5deg) perspective(600px) rotateY(-7deg);
}

.about-logo-plinth::after {
  position: absolute;
  right: 12%;
  bottom: 11%;
  left: 12%;
  height: 16px;
  border-radius: 50%;
  background: rgba(73, 53, 137, 0.17);
  content: '';
  filter: blur(11px);
}

.about-logo-plinth img {
  width: 85%;
  height: 85%;
  object-fit: contain;
  border-radius: 24px;
  transform: rotate(5deg);
}

.about-visual-orbit {
  position: absolute;
  z-index: -1;
  inset: 8px 24px 4px;
  border: 1px solid rgba(111, 87, 189, 0.12);
  border-radius: 50%;
  transform: rotate(27deg);
}

.about-visual-orbit--two {
  inset: 26px 0 20px;
  border-color: rgba(239, 151, 93, 0.18);
  transform: rotate(-21deg);
}

.about-visual-spark {
  position: absolute;
  width: 9px;
  height: 9px;
  border-radius: 2px;
  background: #ee9a5e;
  transform: rotate(45deg);
}

.about-visual-spark--one { top: 29px; right: 22%; }
.about-visual-spark--two { bottom: 34px; left: 19%; width: 6px; height: 6px; background: #7b63ca; }

.about-content {
  max-width: 1100px;
  padding-top: 10px;
  padding-bottom: 58px;
}

.about-company-card {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(230px, 0.34fr);
  align-items: center;
  gap: clamp(25px, 5vw, 58px);
  min-height: 178px;
  padding: 27px clamp(25px, 4.5vw, 52px);
  border: 1px solid rgba(121, 100, 188, 0.17);
  border-radius: 24px;
  background: linear-gradient(115deg, rgba(255, 255, 255, 0.96), rgba(245, 241, 255, 0.94));
  box-shadow: 0 14px 35px rgba(56, 42, 112, 0.075);
}

.about-company-copy {
  padding-left: clamp(20px, 4vw, 52px);
  border-left: 1px solid rgba(121, 100, 188, 0.2);
}

.about-company-copy p {
  margin: 0;
  color: #48455a;
  font-size: clamp(0.98rem, 1.4vw, 1.08rem);
  line-height: 2.1;
}

.about-company-values {
  display: grid;
  justify-items: center;
  gap: 13px;
  color: #3f3373;
  text-align: center;
}

.about-people-icon {
  display: grid;
  width: 64px;
  height: 64px;
  place-items: center;
  border: 1px solid rgba(114, 88, 187, 0.16);
  border-radius: 21px;
  background: linear-gradient(145deg, #f3efff, #fff);
  color: #6b53b2;
  box-shadow: 0 9px 18px rgba(79, 59, 144, 0.1);
}

.about-company-values strong {
  font-size: 1.05rem;
}

.about-licenses {
  padding-top: 56px;
}

.about-section-title {
  display: grid;
  grid-template-columns: minmax(35px, 1fr) minmax(0, auto) minmax(35px, 1fr);
  align-items: center;
  gap: clamp(12px, 2.6vw, 32px);
  margin-bottom: 27px;
  text-align: center;
}

.about-section-title h2 {
  margin: 0 0 8px;
  color: #292449;
  font-size: clamp(1.38rem, 2.5vw, 1.85rem);
}

.about-section-title p {
  max-width: 67ch;
  margin: 0;
  color: #706b80;
  font-size: 0.96rem;
  line-height: 1.9;
}

.about-title-rule {
  height: 2px;
  border-radius: 99px;
  background: linear-gradient(90deg, rgba(231, 145, 84, 0.75), rgba(120, 91, 192, 0.55), transparent);
}

.about-title-rule:last-child {
  transform: rotate(180deg);
}

.about-license-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 22px;
}

.about-license-card {
  min-width: 0;
  padding: 11px;
  border: 1px solid rgba(117, 95, 182, 0.18);
  border-radius: 21px;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 13px 30px rgba(56, 42, 112, 0.075);
}

.about-license-image {
  display: flex;
  min-height: 235px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10px;
  overflow: hidden;
  border: 1px dashed rgba(115, 94, 175, 0.28);
  border-radius: 14px;
  background: linear-gradient(135deg, #f7f4ff, #fbfaff 65%, #f4f8ff);
  color: #716b81;
  text-align: center;
}

.about-license-image img {
  display: block;
  width: 100%;
  height: 100%;
  max-height: 310px;
  object-fit: contain;
  object-position: center;
}

.about-license-caption {
  display: flex;
  min-height: 56px;
  align-items: center;
  gap: 11px;
  margin-top: 9px;
  padding: 11px 15px;
  border-radius: 13px;
  background: linear-gradient(110deg, #6f55bd, #4c65b6);
  color: #fff;
}

.about-license-caption h3 {
  margin: 0;
  font-size: clamp(0.93rem, 1.5vw, 1.04rem);
  line-height: 1.7;
}

.about-contact-banner {
  position: relative;
  display: grid;
  grid-template-columns: minmax(170px, 0.7fr) minmax(0, 1.3fr) auto;
  align-items: center;
  gap: clamp(20px, 4vw, 46px);
  min-height: 200px;
  margin-top: 48px;
  padding: 20px clamp(22px, 4vw, 42px);
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.19);
  border-radius: 25px;
  background: linear-gradient(112deg, #493487 0%, #5946a5 49%, #386cb5 100%);
  box-shadow: 0 18px 37px rgba(53, 43, 115, 0.2);
  color: #fff;
}

.about-contact-banner::before {
  position: absolute;
  top: -135px;
  left: 24%;
  width: 310px;
  height: 310px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  content: '';
}

.about-headset-art {
  position: relative;
  z-index: 1;
  width: min(100%, 210px);
  height: 166px;
  justify-self: center;
  filter: drop-shadow(0 12px 10px rgba(25, 19, 70, 0.22));
}

.about-headset-orbit {
  position: absolute;
  inset: 8px 5px 1px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 49% 51% 45% 55%;
  transform: rotate(-16deg);
}

.about-headset-body {
  position: absolute;
  z-index: 1;
  bottom: 4px;
  left: 42px;
  width: 125px;
  height: 75px;
  border: 3px solid rgba(255, 255, 255, 0.55);
  border-radius: 64px 64px 28px 28px;
  background: linear-gradient(145deg, #a697e5, #7865c2 68%, #5b499f);
  box-shadow: inset -8px -6px 11px rgba(45, 30, 107, 0.22), 0 10px 15px rgba(31, 24, 79, 0.2);
}

.about-headset-face {
  position: absolute;
  z-index: 2;
  top: 40px;
  left: 78px;
  width: 54px;
  height: 64px;
  border: 3px solid rgba(255, 255, 255, 0.62);
  border-radius: 46% 46% 42% 42%;
  background: linear-gradient(135deg, #ffdabf, #f1a780 78%, #dd815f);
  box-shadow: inset -5px -3px 8px rgba(145, 64, 70, 0.13), 0 6px 12px rgba(33, 24, 78, 0.2);
}

.about-headset-band {
  position: absolute;
  z-index: 3;
  top: 22px;
  left: 70px;
  width: 71px;
  height: 66px;
  border: 8px solid transparent;
  border-top-color: #fff;
  border-right-color: #fff;
  border-left-color: #fff;
  border-radius: 50% 50% 0 0;
}

.about-headset-ear {
  position: absolute;
  z-index: 4;
  top: 54px;
  left: 65px;
  width: 19px;
  height: 34px;
  border: 3px solid #fff;
  border-radius: 10px;
  background: linear-gradient(145deg, #927bd8, #5e4b9f);
}

.about-headset-mic {
  position: absolute;
  z-index: 4;
  top: 80px;
  left: 65px;
  width: 40px;
  height: 24px;
  border-right: 3px solid #fff;
  border-bottom: 3px solid #fff;
  border-radius: 0 0 17px 0;
  transform: rotate(-12deg);
}

.about-headset-mic::after {
  position: absolute;
  right: -7px;
  bottom: -6px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ffad68;
  content: '';
}

.about-headset-bubble {
  position: absolute;
  z-index: 5;
  display: grid;
  place-items: center;
  border: 2px solid rgba(255, 255, 255, 0.72);
  border-radius: 13px;
  box-shadow: 0 6px 12px rgba(32, 25, 79, 0.2);
}

.about-headset-bubble--one {
  top: 7px;
  right: 8px;
  width: 45px;
  height: 39px;
  background: linear-gradient(140deg, #f5a66a, #e27e4d);
  transform: rotate(7deg);
}

.about-headset-bubble--two {
  top: 54px;
  right: -2px;
  width: 35px;
  height: 31px;
  background: linear-gradient(140deg, #79b7e3, #4e8ecb);
  transform: rotate(-7deg);
}

.about-contact-copy {
  position: relative;
  z-index: 1;
}

.about-contact-copy h2 {
  margin: 0 0 9px;
  color: #fff;
  font-size: clamp(1.45rem, 2.7vw, 2rem);
}

.about-contact-copy p {
  max-width: 55ch;
  margin: 0;
  color: rgba(255, 255, 255, 0.83);
  line-height: 2;
}

.about-contact-link {
  position: relative;
  z-index: 1;
  display: inline-flex;
  min-height: 50px;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 11px 20px;
  border: 1px solid rgba(255, 255, 255, 0.55);
  border-radius: 14px;
  background: linear-gradient(135deg, #ffbb74, #ee8b4e);
  color: #3d2b57;
  font-weight: 700;
  text-decoration: none;
  box-shadow: 0 9px 17px rgba(35, 23, 72, 0.2);
  transition: transform 180ms ease, box-shadow 180ms ease;
}

.about-contact-link:hover {
  transform: translateY(-3px);
  box-shadow: 0 13px 21px rgba(35, 23, 72, 0.27);
}

.about-contact-link:active {
  transform: translateY(0) scale(0.98);
}

.about-page.dark .about-hero-copy h1,
.about-page.dark .about-section-title h2 {
  color: #e7e0ff;
}

.about-page.dark .about-hero-copy p,
.about-page.dark .about-section-title p {
  color: #c4bdd4;
}

.about-page.dark .about-eyebrow {
  border-color: rgba(169, 148, 224, 0.22);
  background: rgba(39, 34, 57, 0.78);
  color: #d2c3ff;
}

.about-page.dark .about-company-card,
.about-page.dark .about-license-card {
  border-color: rgba(164, 142, 224, 0.2);
  background: linear-gradient(115deg, rgba(31, 29, 43, 0.96), rgba(40, 35, 58, 0.94));
}

.about-page.dark .about-company-copy p,
.about-page.dark .about-company-values {
  color: #e2dcf2;
}

.about-page.dark .about-license-image {
  border-color: rgba(170, 151, 224, 0.25);
  background: linear-gradient(135deg, #29243b, #27263a 65%, #242b3d);
  color: #c9c1db;
}

.about-page.dark .about-people-icon {
  border-color: rgba(170, 151, 224, 0.2);
  background: linear-gradient(145deg, #30294a, #252236);
  color: #c5b3f5;
}

@media (max-width: 760px) {
  .about-hero {
    grid-template-columns: minmax(0, 1fr) minmax(245px, 0.8fr);
    gap: 20px;
    min-height: 300px;
    padding-top: 32px;
  }

  .about-hero-visual {
    min-height: 230px;
  }

  .about-company-card {
    grid-template-columns: minmax(0, 1fr) minmax(170px, 0.42fr);
    gap: 22px;
    padding: 24px;
  }

  .about-contact-banner {
    grid-template-columns: 145px minmax(0, 1fr);
    gap: 18px 24px;
  }

  .about-contact-link {
    grid-column: 2;
    justify-self: start;
  }
}

@media (max-width: 520px) {
  .about-hero {
    grid-template-columns: 1fr;
    gap: 5px;
    min-height: 0;
    padding-top: 29px;
    padding-bottom: 18px;
  }

  .about-hero-copy h1 {
    margin: 12px 0 8px;
    font-size: 2.65rem;
  }

  .about-hero-copy p {
    font-size: 0.98rem;
  }

  .about-hero-visual {
    min-height: 225px;
  }

  .about-logo-plinth {
    width: 154px;
    border-radius: 28px;
  }

  .about-content {
    padding-top: 4px;
    padding-bottom: 40px;
  }

  .about-company-card {
    grid-template-columns: 1fr;
    gap: 19px;
    padding: 21px;
  }

  .about-company-copy {
    padding-left: 0;
    border-left: 0;
  }

  .about-company-values {
    grid-template-columns: auto 1fr;
    justify-items: start;
    align-items: center;
    gap: 12px;
    padding-top: 16px;
    border-top: 1px solid rgba(121, 100, 188, 0.18);
    text-align: right;
  }

  .about-people-icon {
    width: 51px;
    height: 51px;
    border-radius: 16px;
  }

  .about-licenses {
    padding-top: 38px;
  }

  .about-section-title {
    grid-template-columns: 1fr;
    gap: 12px;
    margin-bottom: 20px;
  }

  .about-section-title .about-title-rule {
    width: 76px;
    justify-self: center;
  }

  .about-section-title .about-title-rule:last-child {
    display: none;
  }

  .about-license-grid {
    grid-template-columns: 1fr;
    gap: 15px;
  }

  .about-license-image {
    min-height: 205px;
  }

  .about-contact-banner {
    grid-template-columns: 1fr;
    gap: 8px;
    margin-top: 34px;
    padding: 22px 20px 20px;
    text-align: center;
  }

  .about-headset-art {
    height: 148px;
  }

  .about-contact-copy h2 {
    font-size: 1.5rem;
  }

  .about-contact-link {
    grid-column: 1;
    width: 100%;
  }
}

@media (max-width: 760px) {
  .faq-page .page-hero {
    min-height: 255px;
    padding-top: 43px;
  }

  .faq-hero-art {
    width: clamp(150px, 25vw, 200px);
    flex-basis: clamp(150px, 25vw, 200px);
  }

  .faq-support-card {
    grid-template-columns: minmax(0, 1fr) minmax(160px, 0.7fr);
    gap: 6px;
    margin-top: 40px;
    padding: 25px;
  }
}

@media (max-width: 520px) {
  .faq-page .page-hero {
    min-height: 0;
    flex-direction: column-reverse;
    align-items: stretch;
    gap: 6px;
    padding-top: 30px;
    padding-bottom: 24px;
  }

  .faq-hero-art {
    width: 170px;
    flex-basis: auto;
    align-self: center;
  }

  .faq-page .page-hero h1 {
    margin: 11px 0 10px;
    font-size: 2.2rem;
  }

  .faq-page .page-hero p {
    font-size: 0.97rem;
  }

  .faq-content {
    padding-top: 10px;
    padding-bottom: 48px;
  }

  .faq-question {
    min-height: 66px;
    gap: 12px;
    padding: 13px 14px;
  }

  .faq-toggle {
    width: 32px;
    height: 32px;
    flex-basis: 32px;
  }

  .faq-answer-inner p {
    padding: 0 58px 16px 16px;
    font-size: 0.94rem;
  }

  .faq-support-card {
    grid-template-columns: 1fr;
    gap: 0;
    margin-top: 36px;
    padding: 23px 20px 16px;
    text-align: center;
  }

  .faq-support-kicker {
    margin-bottom: 5px;
  }

  .faq-support-copy p {
    margin-bottom: 14px;
  }

  .faq-support-link {
    width: 100%;
  }

  .faq-support-art {
    height: 166px;
    margin-top: 6px;
  }
}

@media (max-width: 760px) {
  .contact-page .page-hero {
    padding-top: 45px;
  }

  .contact-experience {
    padding-top: 17px;
  }

  .support-note {
    grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
    gap: 20px;
    padding: 0;
  }

  .support-art {
    transform: scale(0.88);
    transform-origin: center;
  }
}

@media (max-width: 520px) {
  .contact-page .page-hero {
    padding-top: 39px;
    padding-bottom: 20px;
  }

  .contact-page .contact-title {
    font-size: clamp(1.8rem, 8vw, 2.3rem);
  }

  .contact-action-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .contact-action,
  .contact-action--phone {
    min-height: 91px;
    gap: 14px;
    padding: 16px;
    border-radius: 21px;
  }

  .action-icon {
    width: 51px;
    height: 51px;
    flex-basis: 51px;
    border-radius: 17px;
  }

  .contact-action--phone .action-copy strong {
    font-size: 1.2rem;
  }

  .support-note {
    grid-template-columns: 1fr;
    gap: 0;
    margin-top: 20px;
    text-align: center;
  }

  .support-art {
    width: 250px;
    height: 195px;
    transform: scale(0.86);
  }

  .support-note-copy {
    padding-bottom: 15px;
  }

  .support-note-line {
    margin-right: auto;
    margin-left: auto;
  }
}

@media (prefers-reduced-motion: reduce) {
  .contact-action,
  .contact-action::after {
    transition-duration: 0.01ms;
  }
}

.about-why-continuation,
.about-story-cards,
.about-transparency-banner {
  position: relative;
  margin-top: 54px;
}

.about-why-continuation {
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(280px, 0.95fr);
  align-items: center;
  gap: clamp(28px, 6vw, 76px);
  min-height: 330px;
  padding: 24px clamp(22px, 4vw, 45px);
  overflow: hidden;
  border: 1px solid rgba(120, 101, 186, 0.12);
  border-radius: 26px;
  background: linear-gradient(110deg, rgba(255, 255, 255, 0.74), rgba(243, 240, 255, 0.83));
}

.about-why-copy {
  position: relative;
  z-index: 1;
}

.about-continuation-eyebrow {
  display: inline-block;
  margin-bottom: 9px;
  color: #775fba;
  font-size: 0.88rem;
  font-weight: 700;
}

.about-why-copy h2 {
  margin: 0 0 12px;
  color: #292449;
  font-size: clamp(1.75rem, 3vw, 2.35rem);
}

.about-why-copy p {
  max-width: 59ch;
  margin: 0;
  color: #625e74;
  line-height: 2.15;
}

.about-why-visual {
  position: relative;
  display: grid;
  min-height: 270px;
  align-content: center;
  justify-items: center;
  gap: 14px;
  overflow: hidden;
  border: 1px solid rgba(125, 105, 190, 0.12);
  border-radius: 24px;
  background: linear-gradient(145deg, rgba(239, 234, 255, 0.92), rgba(249, 247, 255, 0.94) 57%, rgba(235, 245, 255, 0.84));
  color: #7563b4;
}

.about-why-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 174px;
  height: 174px;
  border-radius: 50%;
  background: rgba(170, 151, 233, 0.17);
  filter: blur(24px);
  transform: translate(-50%, -50%);
}

.about-why-orbit {
  position: absolute;
  width: 230px;
  height: 110px;
  border: 1px solid rgba(118, 96, 184, 0.18);
  border-radius: 50%;
  transform: rotate(-24deg);
}

.about-why-orbit--two {
  width: 270px;
  height: 150px;
  border-color: rgba(237, 155, 96, 0.22);
  transform: rotate(24deg);
}

.about-why-placeholder-icon,
.about-why-placeholder-label {
  position: relative;
  z-index: 1;
}

.about-why-placeholder-icon {
  display: grid;
  width: 86px;
  height: 86px;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, 0.9);
  border-radius: 26px;
  background: linear-gradient(145deg, #fff, #eee9ff);
  box-shadow: 0 14px 28px rgba(74, 57, 137, 0.14);
}

.about-why-placeholder-label {
  color: #69627b;
  font-size: 0.88rem;
}

.about-why-spark {
  position: absolute;
  width: 8px;
  height: 8px;
  border-radius: 2px;
  background: #e99a61;
  transform: rotate(45deg);
}

.about-why-spark--one { top: 24%; right: 20%; }
.about-why-spark--two { bottom: 23%; left: 18%; width: 6px; height: 6px; background: #8070c4; }

.about-story-cards {
  display: grid;
  gap: 22px;
  margin-top: 34px;
}

.about-story-card {
  display: grid;
  grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.05fr);
  align-items: stretch;
  gap: clamp(24px, 5vw, 58px);
  min-height: 250px;
  padding: 15px;
  border: 1px solid rgba(120, 101, 186, 0.15);
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.91);
  box-shadow: 0 14px 34px rgba(56, 42, 112, 0.065);
}

.about-story-card--workspace {
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr);
  background: linear-gradient(115deg, rgba(247, 244, 255, 0.97), rgba(255, 255, 255, 0.94));
}

.about-story-photo {
  min-width: 0;
  min-height: 220px;
  overflow: hidden;
  border-radius: 17px;
  background: #eeebf5;
}

.about-story-photo img {
  display: block;
  width: 100%;
  height: 100%;
  min-height: 220px;
  object-fit: cover;
  object-position: center;
  transition: transform 360ms ease;
}

.about-story-card:hover .about-story-photo img {
  transform: scale(1.025);
}

.about-story-copy {
  display: grid;
  align-content: center;
  justify-items: start;
  padding: clamp(18px, 3vw, 34px);
}

.about-story-icon {
  display: grid;
  width: 48px;
  height: 48px;
  place-items: center;
  margin-bottom: 12px;
  border: 1px solid rgba(116, 91, 188, 0.16);
  border-radius: 15px;
  background: #f2efff;
  color: #7058b4;
}

.about-story-copy h2 {
  margin: 0 0 9px;
  color: #30284e;
  font-size: clamp(1.35rem, 2.2vw, 1.72rem);
}

.about-story-copy p {
  max-width: 51ch;
  margin: 0;
  color: #676276;
  line-height: 2.05;
}

.about-transparency-banner {
  display: grid;
  grid-template-columns: minmax(150px, 0.7fr) minmax(0, 1.3fr) auto;
  align-items: center;
  gap: clamp(22px, 4vw, 48px);
  min-height: 205px;
  margin-top: 37px;
  padding: 20px clamp(22px, 4vw, 42px);
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 25px;
  background: linear-gradient(112deg, #493487 0%, #5946a5 49%, #386cb5 100%);
  box-shadow: 0 18px 37px rgba(53, 43, 115, 0.2);
  color: #fff;
}

.about-transparency-banner::before {
  position: absolute;
  top: -147px;
  left: 27%;
  width: 320px;
  height: 320px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  content: '';
}

.about-speaker-placeholder {
  position: relative;
  width: 175px;
  height: 155px;
  justify-self: center;
}

.about-speaker-halo {
  position: absolute;
  inset: 8px 3px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  transform: rotate(-20deg);
}

.about-speaker-body {
  position: absolute;
  z-index: 2;
  top: 52px;
  left: 29px;
  width: 37px;
  height: 48px;
  border: 2px solid rgba(255, 255, 255, 0.8);
  border-radius: 9px 4px 4px 9px;
  background: linear-gradient(145deg, #ffa86d, #e77c4e);
  transform: rotate(-10deg);
}

.about-speaker-cone {
  position: absolute;
  z-index: 2;
  top: 44px;
  left: 60px;
  width: 57px;
  height: 64px;
  border: 2px solid rgba(255, 255, 255, 0.77);
  background: linear-gradient(145deg, #df9be2, #a579d3 58%, #f5a05e);
  clip-path: polygon(0 24%, 100% 0, 100% 100%, 0 76%);
  filter: drop-shadow(0 7px 8px rgba(34, 23, 77, 0.25));
}

.about-speaker-handle {
  position: absolute;
  z-index: 1;
  top: 91px;
  left: 75px;
  width: 22px;
  height: 37px;
  border: 2px solid rgba(255, 255, 255, 0.74);
  border-top: 0;
  border-radius: 0 0 7px 7px;
  background: linear-gradient(145deg, #f49a5d, #d87049);
  transform: rotate(-10deg);
}

.about-speaker-wave {
  position: absolute;
  top: 54px;
  left: 111px;
  width: 30px;
  height: 47px;
  border-right: 3px solid #ffb56e;
  border-radius: 0 50% 50% 0;
  transform: rotate(-2deg);
}

.about-speaker-wave--two {
  top: 44px;
  left: 120px;
  width: 43px;
  height: 68px;
  border-right-width: 2px;
  opacity: 0.68;
}

.about-transparency-copy,
.about-transparency-link {
  position: relative;
  z-index: 1;
}

.about-transparency-copy h2 {
  margin: 0 0 9px;
  color: #fff;
  font-size: clamp(1.35rem, 2.5vw, 1.9rem);
  line-height: 1.65;
}

.about-transparency-copy p {
  max-width: 54ch;
  margin: 0;
  color: rgba(255, 255, 255, 0.84);
  line-height: 2;
}

.about-transparency-link {
  display: inline-flex;
  min-height: 50px;
  align-items: center;
  justify-content: center;
  gap: 11px;
  padding: 10px 19px;
  border: 1px solid rgba(255, 255, 255, 0.52);
  border-radius: 14px;
  background: linear-gradient(135deg, #ffbb74, #ee8b4e);
  color: #3d2b57;
  font-weight: 700;
  text-decoration: none;
  box-shadow: 0 9px 17px rgba(35, 23, 72, 0.2);
  transition: transform 180ms ease, box-shadow 180ms ease;
}

.about-transparency-link:hover {
  transform: translateY(-3px);
  box-shadow: 0 13px 21px rgba(35, 23, 72, 0.27);
}

.about-transparency-link:active {
  transform: translateY(0) scale(0.98);
}

.about-page.dark .about-why-continuation,
.about-page.dark .about-story-card {
  border-color: rgba(164, 142, 224, 0.2);
  background: linear-gradient(110deg, rgba(31, 29, 43, 0.89), rgba(40, 35, 58, 0.9));
}

.about-page.dark .about-why-copy h2,
.about-page.dark .about-story-copy h2 {
  color: #e7e0ff;
}

.about-page.dark .about-why-copy p,
.about-page.dark .about-story-copy p {
  color: #c4bdd4;
}

.about-page.dark .about-why-visual {
  border-color: rgba(164, 142, 224, 0.2);
  background: linear-gradient(145deg, #29243b, #27263a 57%, #242b3d);
}

.about-page.dark .about-why-placeholder-label {
  color: #c9c1db;
}

.about-page.dark .about-story-icon {
  border-color: rgba(170, 151, 224, 0.2);
  background: linear-gradient(145deg, #30294a, #252236);
  color: #c5b3f5;
}

@media (max-width: 760px) {
  .about-why-continuation {
    grid-template-columns: minmax(0, 1fr) minmax(230px, 0.85fr);
    gap: 22px;
    min-height: 290px;
    padding: 19px;
  }

  .about-why-visual {
    min-height: 235px;
  }

  .about-story-card,
  .about-story-card--workspace {
    gap: 18px;
  }

  .about-transparency-banner {
    grid-template-columns: 135px minmax(0, 1fr);
    gap: 15px 22px;
  }

  .about-transparency-link {
    grid-column: 2;
    justify-self: start;
  }
}

@media (max-width: 520px) {
  .about-why-continuation {
    grid-template-columns: 1fr;
    gap: 15px;
    margin-top: 36px;
    padding: 21px;
  }

  .about-why-visual {
    min-height: 208px;
  }

  .about-story-cards {
    gap: 16px;
    margin-top: 25px;
  }

  .about-story-card,
  .about-story-card--workspace {
    grid-template-columns: 1fr;
    gap: 7px;
    padding: 10px;
  }

  .about-story-card--workspace .about-story-copy {
    order: 2;
  }

  .about-story-card--workspace .about-story-photo {
    order: 1;
  }

  .about-story-photo,
  .about-story-photo img {
    min-height: 190px;
  }

  .about-story-copy {
    padding: 17px 14px 20px;
  }

  .about-story-icon {
    margin-bottom: 10px;
  }

  .about-transparency-banner {
    grid-template-columns: 1fr;
    gap: 7px;
    margin-top: 25px;
    padding: 18px 20px 21px;
    text-align: center;
  }

  .about-speaker-placeholder {
    width: 155px;
    height: 140px;
  }

  .about-transparency-copy h2 {
    font-size: 1.45rem;
  }

  .about-transparency-link {
    grid-column: 1;
    width: 100%;
  }
}
</style>
